// SimulatorResults.js — Tab panel showing simulation results:
// Summary, Cash Flows, Decomposition. Shows SIMULATION watermark.

import React, { useState } from 'react';
import { useSimulator } from '../../context/SimulatorContext';
import CashflowLedger from './CashflowLedger';
import { TrendingUp, TrendingDown, ArrowRightLeft, Receipt, Shield, DollarSign } from 'lucide-react';

const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'cashflows', label: 'Cash Flows' },
  { key: 'decomposition', label: 'Return Breakdown' },
];

function formatCurrency(value, ccy = 'USD') {
  if (value == null) return '—';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e6) return `${sign}${ccy} ${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${ccy} ${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${sign}${ccy} ${abs.toFixed(2)}`;
}

function formatPct(value) {
  if (value == null) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function MetricCard({ label, value, subValue, icon: Icon, positive }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${positive ? 'text-green-500' : positive === false ? 'text-red-500' : 'text-gray-400'}`} />}
      </div>
      <div className={`text-lg font-semibold ${positive ? 'text-green-700' : positive === false ? 'text-red-700' : 'text-gray-900'}`}>
        {value}
      </div>
      {subValue && <div className="text-xs text-gray-500 mt-1">{subValue}</div>}
    </div>
  );
}

function SummaryTab({ summary, sourceCurrency }) {
  if (!summary) return null;

  const netPct = summary.netReturnPct;
  const isPositive = summary.netReturnBase > 0;

  return (
    <div className="space-y-4">
      {/* Key metrics grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <MetricCard
          label="Net Return"
          value={formatCurrency(summary.netReturnBase, sourceCurrency)}
          subValue={formatPct(netPct)}
          icon={isPositive ? TrendingUp : TrendingDown}
          positive={isPositive}
        />
        <MetricCard
          label="Investment"
          value={formatCurrency(summary.investmentAmount, sourceCurrency)}
          subValue={`${summary.tenorDays}-day ${summary.instrument}`}
          icon={DollarSign}
        />
        <MetricCard
          label="Entry FX Rate"
          value={`${summary.entryFxRate?.toFixed(2)}`}
          subValue={`${sourceCurrency}/KES`}
          icon={ArrowRightLeft}
        />
        <MetricCard
          label="Exit FX Rate"
          value={`${summary.exitFxRate?.toFixed(2)}`}
          subValue={`${sourceCurrency}/KES`}
          icon={ArrowRightLeft}
        />
        <MetricCard
          label="WHT Paid"
          value={formatCurrency(summary.totalWhtBase, sourceCurrency)}
          subValue="15% withholding tax"
          icon={Receipt}
          positive={false}
        />
        <MetricCard
          label="Total Fees"
          value={formatCurrency(summary.totalFeesBase, sourceCurrency)}
          subValue="Platform + custody + FX spread"
          icon={Receipt}
          positive={false}
        />
      </div>

      {/* Forward protection info */}
      {summary.forwardMode !== 'none' && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">FX Forward Protection</span>
          </div>
          <div className="text-sm text-blue-700">
            <span>Mode: {summary.forwardMode === 'market' ? 'Market CIP forward' : 'Custom premium'}</span>
            {summary.forwardRate && (
              <span className="ml-4">Forward rate: {summary.forwardRate.toFixed(4)}</span>
            )}
          </div>
        </div>
      )}

      {/* Period info */}
      <div className="text-xs text-gray-400 font-mono">
        {summary.startDate} to {summary.endDate} | {summary.rolloverMode === 'off' ? 'Single cycle' : `Rollover: ${summary.rolloverMode.replace(/_/g, ' ')}`}
      </div>
    </div>
  );
}

function DecompositionTab({ decomposition, sourceCurrency }) {
  if (!decomposition) return null;

  const lines = [
    { label: 'Gross yield (base currency)', value: decomposition.grossYieldBase, positive: true },
    { label: 'Withholding tax', value: -decomposition.whtBase, positive: false },
    { label: 'Fees (platform + custody + FX spread)', value: -decomposition.feesBase, positive: false },
    { label: 'FX gain / loss', value: decomposition.fxGainLoss, positive: decomposition.fxGainLoss >= 0 },
    { label: 'Hedge cost', value: -decomposition.hedgeCost, positive: false },
  ];

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-2 text-xs text-gray-500 uppercase">Component</th>
              <th className="text-right px-4 py-2 text-xs text-gray-500 uppercase">Amount ({sourceCurrency})</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i} className="border-b border-gray-50">
                <td className="px-4 py-2 text-gray-700">{line.label}</td>
                <td className={`px-4 py-2 text-right font-mono ${line.positive ? 'text-green-700' : 'text-red-600'}`}>
                  {formatCurrency(line.value, sourceCurrency)}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td className="px-4 py-2 text-gray-900">Net return</td>
              <td className={`px-4 py-2 text-right font-mono ${decomposition.netReturn >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                {formatCurrency(decomposition.netReturn, sourceCurrency)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SimulatorResults() {
  const { results, error } = useSimulator();
  const [activeTab, setActiveTab] = useState('summary');

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Configure parameters and run a simulation to see results</p>
        </div>
      </div>
    );
  }

  const { summary, cashflows } = results;
  const sourceCurrency = summary?.sourceCurrency || 'USD';

  return (
    <div className="relative">
      {/* SIMULATION watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] z-0">
        <span className="text-8xl font-black text-gray-900 rotate-[-15deg] select-none">SIMULATION</span>
      </div>

      <div className="relative z-10">
        {/* Tab bar */}
        <div className="flex border-b border-gray-200 mb-4">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'summary' && <SummaryTab summary={summary} sourceCurrency={sourceCurrency} />}
        {activeTab === 'cashflows' && <CashflowLedger cashflows={cashflows} sourceCurrency={sourceCurrency} />}
        {activeTab === 'decomposition' && <DecompositionTab decomposition={summary?.decomposition} sourceCurrency={sourceCurrency} />}
      </div>
    </div>
  );
}
